/**
 * Pure defaults for per-company settings. No database import, so the Dexie
 * v2 upgrade step and `createCompany` build identical rows without a
 * db <-> domain import cycle.
 */
import type { Company, CompanySettings, EntityType, IsoDateTime } from '../types';

/** 10 000 kr: above this an inbox item is never batch-booked without a look. */
export const DEFAULT_BATCH_THRESHOLD_ORE = 1_000_000;
/** 50 000 kr: above this an item is always `needs_you`. */
export const ALWAYS_NEEDS_YOU_ORE = 5_000_000;

/** Onboarding default for "Är ni momsregistrerade?" (spec section 5). */
export function defaultVatRegistered(entity: EntityType): boolean {
  switch (entity) {
    case 'aktiebolag':
    case 'enskild_firma':
      return true;
    case 'ideell_forening':
    case 'bostadsrattsforening':
      return false;
    default: {
      const exhaustive: never = entity;
      throw new Error(`Unknown entity type ${String(exhaustive)}`);
    }
  }
}

export type CompanySettingsOverrides = Partial<
  Pick<CompanySettings, 'vatRegistered' | 'vatPeriod' | 'accountingMethod' | 'defaultPaymentSourceId' | 'arsavgiftAccount' | 'onboarding'>
>;

/** Pure default row; shared by `createCompany` and the Dexie v2 upgrade. */
export function defaultCompanySettings(
  company: Pick<Company, 'id' | 'entityType'>,
  now: IsoDateTime = new Date().toISOString(),
  overrides: CompanySettingsOverrides = {},
): CompanySettings {
  const base: CompanySettings = {
    id: company.id,
    companyId: company.id,
    vatRegistered: defaultVatRegistered(company.entityType),
    vatPeriod: 'quarter',
    accountingMethod: 'cash',
    batchThresholdOre: DEFAULT_BATCH_THRESHOLD_ORE,
    defaultPaymentSourceId: null,
    onboarding: { completedAt: null, driveSkippedAt: null, aiSkippedAt: null },
    updatedAt: now,
  };
  if (company.entityType === 'bostadsrattsforening') base.arsavgiftAccount = 3910;
  for (const [k, v] of Object.entries(overrides)) {
    if (v !== undefined) (base as unknown as Record<string, unknown>)[k] = v;
  }
  return base;
}
